import type { GenericFieldConfig } from '@neo/blocks/forms/types'
import { capitalize } from 'lodash'
import { NodevarList } from 'v2/pages/flow/components/nodevar-list'
import type { RProviders } from 'v2/pages/flow/sidebar/types'
import {
  imageGenerationConfigSchema,
  invalidSelectValueSchema,
} from '../../config/validation.config'
import type { AiSkillsNodeData } from '../../types'
import { SkillInstructionsTextArea } from '../../utils/skill-instructions-textarea'

type FormValues = AiSkillsNodeData['config']

export const getImageGenerationFields = (
  nodeVars: string[],
  providers?: RProviders,
): GenericFieldConfig<FormValues> => [
  {
    name: 'image_generation_config.model_id',
    componentType: 'selectWithGroups',
    onChangeListenTo: ['tool_type'],
    zodValidation: invalidSelectValueSchema(
      imageGenerationConfigSchema.shape.model_id,
      providers?.image_gen_providers?.flatMap((provider) =>
        provider?.models?.map((model) => model.id),
      ) ?? [],
    ),
    eventDataExtractor: (value) => value,
    afterOnChange: (value, _, form) => {
      if (!form) return
      const provider = providers?.image_gen_providers.find((p) =>
        p.models.some((model) => model.id === value.image_generation_config?.model_id),
      )
      form.setFieldValue('image_generation_config.provider_id', provider?.id ?? '')
      form.validateAllFields('change')
    },
    getDynamicProps: () => ({
      inputProps: {
        placeholder: 'Select model provider',
        label: 'Model Provider',
        groups: providers?.image_gen_providers?.map((provider) => ({
          label: provider.label,
          options: provider?.models?.map((model) => ({
            label: (
              <div className="flex items-center gap-2">
                <img src={provider.meta.icon_url} alt={provider.label} className="w-4 h-4" />
                {capitalize(`${model.label}`)}
              </div>
            ),
            value: model.id,
            searchValue: `${provider.label} ${model.label}`,
          })),
        })),
      },
    }),
    visible: (state) => state.tool_type === 'image_generation',
  },
  {
    name: 'image_generation_config.instructions',
    label: 'Instructions',
    type: 'custom',
    onChangeListenTo: ['tool_type'],
    component: SkillInstructionsTextArea,
    eventDataExtractor: (e) => e,
    zodValidation: imageGenerationConfigSchema.shape.instructions,
    inputProps: {
      placeholder: 'ex: Generate a product banner with vibrant colors and promotional messaging',
      rows: 4,
      endIcon: (
        <NodevarList
          nodevars={nodeVars}
          inputElementSelector="#image_generation_config.instructions .ql-ref"
        />
      ),
      'data-testid': 'textarea-skill-instructions',
    },
    visible: (state) => state.tool_type === 'image_generation',
  },
]
